import type React from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { POST_LOGIN_REDIRECT_KEY } from '../App';

/**
 * Where to send the user after a successful login: the path they originally tried to
 * reach before being bounced to /login (see App.tsx's RedirectToLogin), if any, otherwise
 * the existing default.
 */
function resolvePostLoginDestination(): string {
  const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return saved || '/transactions';
}

export const LoginSuccessPage: React.FC = () => {
  const navigate = useNavigate();

  // Lazy ref initialization (React's documented-safe pattern for this): resolving the
  // destination is a one-time, destructive read (it clears the saved sessionStorage key),
  // so it must not run as useRef's own initializer argument - that expression is evaluated
  // on every render, including React StrictMode's dev-mode double-invoke of the component
  // body, and the second call would see an already-cleared value. Guarding the assignment
  // behind a `.current === null` check instead is safe under the double-invoke, because the
  // ref object itself (unlike a plain variable) is shared across both synthetic calls.
  const destinationRef = useRef<string | null>(null);
  if (destinationRef.current === null) {
    destinationRef.current = resolvePostLoginDestination();
  }
  const destination = destinationRef.current;

  useEffect(() => {
    // Redirect to the destination after showing success message briefly
    const timer = setTimeout(() => {
      navigate(destination);
    }, 2000);

    return () => clearTimeout(timer);
  }, [navigate, destination]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Login Successful!
          </h2>

          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome to the Financial Costpoint Management System
          </p>

          <div className="mt-6">
            <div className="bg-white py-6 px-6 shadow rounded-lg">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-gray-600">Redirecting to dashboard...</span>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={() => navigate(destination)}
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
            >
              Continue manually →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
